import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
//
import { AuthContext } from './auth-context';
import {
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  confirmPasswordReset,
  onAuthStateChanged,
  onIdTokenChanged
} from 'firebase/auth';
import { firebaseAuth } from 'src/hooks/use-firebase-app';

// ----------------------------------------------------------------------

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [firebaseUserDetails, setFirebaseUserDetails] = useState(null);

  const tokenRefreshTimer = useRef(null);

  // VALIDATE USER SESSION
  useEffect(() => {
    console.log('Registering auth state listener');

    const unsubscribe = onAuthStateChanged(firebaseAuth, (currentUser) => {
      if (currentUser) {
        console.log("Current user detection:", currentUser);
        setFirebaseUserDetails(currentUser);
      } else {
        console.log("User logged out");
        setFirebaseUserDetails(null);
      }

      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [firebaseAuth]);

  // REFRESH FIREBASE TOKEN
  useEffect(() => {
    const clearTokenRefreshTimer = () => {
      if (tokenRefreshTimer.current) {
        clearTimeout(tokenRefreshTimer.current);
        tokenRefreshTimer.current = null;
      }
    };

    const scheduleTokenRefresh = async () => {
        clearTokenRefreshTimer();

        const user = firebaseAuth.currentUser;

        if (!user) {
          return;
        }

        try {
          const tokenResult = await user.getIdTokenResult();

          const expirationTime = new Date(tokenResult.expirationTime).getTime();

          const currentTime = Date.now();

          // Refresh 5 minutes before expiration
          const refreshBeforeExpiry = 5 * 60 * 1000;

          const refreshIn = expirationTime - currentTime - refreshBeforeExpiry;

          if (refreshIn <= 0) {
            await user.getIdToken(true);
            await scheduleTokenRefresh();

            return;
          }

          console.log(
            `Firebase ID token refresh scheduled in ${
                Math.round(refreshIn / 1000)
            } seconds`
          );

          tokenRefreshTimer.current = setTimeout(
            async () => {
              try {
                const currentUser = firebaseAuth.currentUser;

                if (!currentUser) {
                  return;
                }

                await currentUser.getIdToken(true);

                console.log('Firebase ID token refreshed');

                await scheduleTokenRefresh();
              } catch (error) {
                console.error(
                  'Failed to refresh Firebase ID token:',
                  error
                );
              }
            },
            refreshIn
          );
        } catch (error) {
          console.error(
            'Failed to schedule Firebase ID token refresh:',
            error
          );
        }
    };

    const unsubscribe = onIdTokenChanged(
        firebaseAuth,
        async (user) => {
            clearTokenRefreshTimer();

            if (!user) {
                return;
            }

            await scheduleTokenRefresh();
        }
    );

    return () => {
      unsubscribe();
      clearTokenRefreshTimer();
    };
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    console.log("Need logout");
    
    signOut(firebaseAuth);
    setFirebaseUserDetails(null);
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const firebaseResponse = await signInWithEmailAndPassword(firebaseAuth, email, password);
    console.log({ firebaseResponse });

    const accessToken = await firebaseAuth.currentUser?.getIdToken();

    return accessToken;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    console.log("Forgot password requested for " + email);
    const response = await sendPasswordResetEmail(firebaseAuth, email);
    return response;
  }, []);

  const verifyOldPassword = useCallback(async (currentPassword) => {
    const credential = EmailAuthProvider.credential(firebaseAuth.currentUser.email, currentPassword);

    try {
      await reauthenticateWithCredential(firebaseAuth.currentUser, credential);
      return null;
    } catch (error) {
      console.error("Error during re-authentication or password update:", error.message);
      console.log(error.code);
      return error.code;
    }
  }, []);

  const requestUpdatePassword = useCallback(async (newPassword) => {
    console.log("Update password requested");
    const response = await updatePassword(firebaseAuth.currentUser, newPassword);
    return response;
  }, []);

  // RESET PASSWORD
  const firebaseResetPassword = useCallback(async (oobCode, newPassword) => {
    try {
      return confirmPasswordReset(firebaseAuth, oobCode, newPassword);
    } catch (error) {
      console.error("Error during password reset:", error);
      throw error;
    }
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = firebaseUserDetails ? 'authenticated' : 'unauthenticated';

  const memoizedValue = useMemo(
    () => ({
      method: 'jwt',
      loading: loading,
      authenticated: checkAuthenticated === 'authenticated',
      unauthenticated: checkAuthenticated === 'unauthenticated',
      //
      login,
      forgotPassword,
      requestUpdatePassword,
      verifyOldPassword,
      logout,
      firebaseResetPassword
    }),
    [
      login, 
      loading,
      checkAuthenticated,
      logout, 
      forgotPassword, 
      requestUpdatePassword, 
      verifyOldPassword
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};